import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FiSend, FiX, FiSave, FiPaperclip, FiUpload, FiFile, FiTrash2, FiChevronLeft } from 'react-icons/fi';
import { FaEdit } from 'react-icons/fa';
import { EmailTemplate, EmailSignature, EmailAttachment, EmailRecipient, ComposeEmailProps } from '../../types/email';
import { Document } from '../../types/document';
import EmailEditor from '../../components/EmailEditor';
import RecipientSelector from '../../components/RecipientSelector';

interface Patient {
	id: number;
	first_name: string;
	last_name: string;
	date_of_birth?: string;
	medical_record_number?: string;
	email?: string;
	contact_email?: string;
}

const ComposeEmail: React.FC<ComposeEmailProps> = ({ 
	onClose, 
	prefilledPatient,
	prefilledSubject,
	replyToEmail 
}) => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const editorRef = useRef<any>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	
	// Email form state
	const [fromEmail, setFromEmail] = useState('');
	const [toRecipients, setToRecipients] = useState<EmailRecipient[]>([]);
	const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>([]);
	const [bccRecipients, setBccRecipients] = useState<EmailRecipient[]>([]);
	const [subject, setSubject] = useState('');
	const [body, setBody] = useState('');
	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [selectedSignatureId, setSelectedSignatureId] = useState('');
	const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
	const [selectedPatients, setSelectedPatients] = useState<Patient[]>([]);
	const [patientDocuments, setPatientDocuments] = useState<Map<number, Document[]>>(new Map());
	const [loadingDocuments, setLoadingDocuments] = useState<Set<number>>(new Set());
	const [documentFilter, setDocumentFilter] = useState('');
	const [leftSidebarWidth, setLeftSidebarWidth] = useState(320);
	const [rightSidebarWidth, setRightSidebarWidth] = useState(400);
	const [editingAttachment, setEditingAttachment] = useState<number | null>(null);
	const [editedFileName, setEditedFileName] = useState('');
	
	// Patient search state (separate from recipients)
	const [patientSearch, setPatientSearch] = useState('');
	const [patientResults, setPatientResults] = useState<Patient[]>([]);
	const [showPatientResults, setShowPatientResults] = useState(false);
	
	// UI State
	const [showCc, setShowCc] = useState(false);
	const [showBcc, setShowBcc] = useState(false);
	const [sending, setSending] = useState(false);
	const [savingDraft, setSavingDraft] = useState(false);
	
	// Data from API
	const [templates, setTemplates] = useState<EmailTemplate[]>([]);
	const [signatures, setSignatures] = useState<EmailSignature[]>([]);
	const [clinicSettings, setClinicSettings] = useState<any>(null);
	const [userEmail, setUserEmail] = useState('');

	// Load initial data
	useEffect(() => {
		loadInitialData();
	}, [token]);
	
	// Patient search with debounce (separate from recipients)
	useEffect(() => {
		if (patientSearch.length >= 2 && token) {
			const timer = setTimeout(async () => {
				try {
					const response = await apiCall(
						`${ENDPOINTS.PATIENTS.PATIENTS}?search=${encodeURIComponent(patientSearch)}`,
						{ method: 'GET' },
						token
					);
					
					if (response.ok) {
						const data = await response.json();
						const patients = data.results || data || [];
						
						// Client-side filtering
						const searchLower = patientSearch.toLowerCase().trim();
						const filtered = patients.filter((patient: Patient) => {
							const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
							const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
							const mrn = (patient.medical_record_number || '').toLowerCase();
							
							return fullName.includes(searchLower) || 
								   reverseName.includes(searchLower) || 
								   mrn.includes(searchLower);
						});
						
						setPatientResults(filtered);
						setShowPatientResults(filtered.length > 0);
					}
				} catch (error) {
					console.error('Error searching patients:', error);
				}
			}, 300);
			
			return () => clearTimeout(timer);
		} else {
			setPatientResults([]);
			setShowPatientResults(false);
		}
	}, [patientSearch, token]);

	// Handle prefilled data
	useEffect(() => {
		if (prefilledPatient) {
			const recipient: EmailRecipient = {
				id: prefilledPatient.id,
				email: prefilledPatient.email,
				name: `${prefilledPatient.first_name} ${prefilledPatient.last_name}`,
				type: 'patient'
			};
			setToRecipients([recipient]);
		}

		if (prefilledSubject) {
			setSubject(prefilledSubject);
		}

		if (replyToEmail) {
			// Set up reply
			setSubject(`Re: ${replyToEmail.subject}`);
			const replyBody = `\n\n---Original Message---\nFrom: ${replyToEmail.from_name} <${replyToEmail.from_email}>\nSubject: ${replyToEmail.subject}\n\n${replyToEmail.body}`;
			setBody(replyBody);
		}
	}, [prefilledPatient, prefilledSubject, replyToEmail]);

	const loadInitialData = async () => {
		if (!token) return;

		try {
			// Load templates
			const templatesResponse = await apiCall(ENDPOINTS.EMAIL.TEMPLATES, { method: 'GET' }, token);
			if (templatesResponse.ok) {
				const data = await templatesResponse.json();
				const activeTemplates = (data.results || data || []).filter((t: EmailTemplate) => t.is_active);
				setTemplates(activeTemplates);
			}

			// Load signatures
			const signaturesResponse = await apiCall(ENDPOINTS.EMAIL.SIGNATURES, { method: 'GET' }, token);
			if (signaturesResponse.ok) {
				const data = await signaturesResponse.json();
				const allSignatures = data.results || data || [];
				setSignatures(allSignatures);
				
				// Set default signature
				const defaultSig = allSignatures.find((s: EmailSignature) => s.is_default);
				if (defaultSig) {
					setSelectedSignatureId(defaultSig.id);
				}
			}

			// Load clinic settings
			const settingsResponse = await apiCall(ENDPOINTS.EMAIL.SETTINGS, { method: 'GET' }, token);
			if (settingsResponse.ok) {
				const settings = await settingsResponse.json();
				setClinicSettings(settings);
				
				// Set from email based on settings
				if (settings.use_clinic_email) {
					setFromEmail(settings.clinic_email || settings.default_from_email);
				} else {
					// Get user's email
					const userResponse = await apiCall(ENDPOINTS.AUTH.USER, { method: 'GET' }, token);
					if (userResponse.ok) {
						const userData = await userResponse.json();
						setUserEmail(userData.email);
						setFromEmail(userData.email);
					}
				}
			}
		} catch (error) {
			console.error('Error loading initial data:', error);
		}
	};

	const handleTemplateSelect = (templateId: string) => {
		const template = templates.find(t => t.id === templateId);
		if (!template) return;

		// Apply template
		setSelectedTemplateId(templateId);
		setSubject(template.subject_template);
		setBody(template.body_template);
	};

	const handleSignatureSelect = (signatureId: string) => {
		setSelectedSignatureId(signatureId);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files) return;

		const newAttachments: EmailAttachment[] = Array.from(files).map(file => ({
			file,
			file_name: file.name,
			file_size: file.size,
			file_type: file.type
		}));

		setAttachments([...attachments, ...newAttachments]);
	};

	const handleRemoveAttachment = (index: number) => {
		setAttachments(attachments.filter((_, i) => i !== index));
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	const handleSendEmail = async () => {
		if (!token) return;

		// Validation
		if (toRecipients.length === 0) {
			alert('Please add at least one recipient');
			return;
		}

		if (!subject.trim()) {
			alert('Please enter a subject');
			return;
		}

		setSending(true);

		try {
			// Get final body with signature
			let finalBody = body;
			if (selectedSignatureId) {
				const signature = signatures.find(s => s.id === selectedSignatureId);
				if (signature) {
					finalBody += `\n\n${signature.content}`;
				}
			}

			// Prepare email data
			const emailData = {
				from_email: fromEmail,
				to_recipients: toRecipients.map(r => r.email),
				cc_recipients: ccRecipients.map(r => r.email),
				bcc_recipients: bccRecipients.map(r => r.email),
				subject,
				body: finalBody,
				signature_id: selectedSignatureId || undefined,
				template_id: selectedTemplateId || undefined,
			};

			// Create FormData for file uploads
			const formData = new FormData();
			formData.append('data', JSON.stringify(emailData));

			// Add attachments
			attachments.forEach((attachment, index) => {
				if (attachment.file) {
					formData.append(`attachment_${index}`, attachment.file);
				}
			});

			const response = await apiCall(ENDPOINTS.EMAIL.SEND, {
				method: 'POST',
				body: formData,
			}, token);

			if (response.ok) {
				alert('Email sent successfully!');
				if (onClose) {
					onClose();
				} else {
					navigate('/email');
				}
			} else {
				const error = await response.json();
				alert(`Failed to send email: ${error.message || 'Unknown error'}`);
			}
		} catch (error) {
			console.error('Error sending email:', error);
			alert('Failed to send email');
		} finally {
			setSending(false);
		}
	};

	const handleSaveDraft = async () => {
		if (!token) return;

		setSavingDraft(true);

		try {
			const emailData = {
				from_email: fromEmail,
				to_recipients: toRecipients,
				cc_recipients: ccRecipients,
				bcc_recipients: bccRecipients,
				subject,
				body,
				signature_id: selectedSignatureId || undefined,
				template_id: selectedTemplateId || undefined,
				status: 'draft'
			};

			const response = await apiCall(ENDPOINTS.EMAIL.CREATE, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(emailData),
			}, token);

			if (response.ok) {
				alert('Draft saved successfully!');
			} else {
				alert('Failed to save draft');
			}
		} catch (error) {
			console.error('Error saving draft:', error);
			alert('Failed to save draft');
		} finally {
			setSavingDraft(false);
		}
	};

	const handleCancel = () => {
		if (window.confirm('Are you sure you want to discard this email?')) {
			if (onClose) {
				onClose();
			} else {
				navigate('/email');
			}
		}
	};

	const fetchPatientDocuments = async (patientId: number) => {
		if (!token || loadingDocuments.has(patientId) || patientDocuments.has(patientId)) return;
		
		console.log(`Fetching documents for patient ${patientId}`);
		setLoadingDocuments(new Set(loadingDocuments).add(patientId));
		
		try {
			const url = `${ENDPOINTS.DOCUMENTS.LIST}?patient_id=${patientId}`;
			console.log(`API URL: ${url}`);
			const response = await apiCall(
				url,
				{ method: 'GET' },
				token
			);
			
			if (response.ok) {
				const data = await response.json();
				const docs = data.results || data || [];
				console.log(`Received ${docs.length} documents for patient ${patientId}:`, docs);
				setPatientDocuments(new Map(patientDocuments).set(patientId, docs));
			} else {
				console.error(`Failed to fetch documents: ${response.status} ${response.statusText}`);
			}
		} catch (error) {
			console.error('Error fetching patient documents:', error);
		} finally {
			const newLoading = new Set(loadingDocuments);
			newLoading.delete(patientId);
			setLoadingDocuments(newLoading);
		}
	};

	const handlePatientSelect = (patient: Patient) => {
		// Check if already selected
		if (selectedPatients.some(p => p.id === patient.id)) {
			alert('This patient is already selected');
			return;
		}
		
		setSelectedPatients([...selectedPatients, patient]);
		setPatientSearch('');
		setShowPatientResults(false);
		setPatientResults([]);
		
		// Fetch documents for this patient
		if (!patientDocuments.has(patient.id)) {
			fetchPatientDocuments(patient.id);
		}
	};

	const handleAddDocumentToAttachments = async (doc: Document, patientName: string) => {
		if (!token) return;
		
		// Check if already attached
		if (attachments.some(a => a.file_name === doc.file_name && a.file_size === doc.file_size)) {
			alert('This document is already attached');
			return;
		}
		
		try {
			// Fetch the document content/file
			const response = await apiCall(
				`${ENDPOINTS.DOCUMENTS.DOWNLOAD}${doc.id}/download/`,
				{ method: 'GET' },
				token
			);
			
			if (response.ok) {
				const blob = await response.blob();
				const file = new File([blob], doc.file_name, { type: doc.content_type || 'application/octet-stream' });
				
				const reader = new FileReader();
				reader.onloadend = () => {
					const base64 = (reader.result as string).split(',')[1];
					const newAttachment: EmailAttachment = {
						file_name: doc.file_name,
						file_size: doc.file_size,
						file_type: doc.content_type || doc.file_type,
						file_data: base64,
						source: 'patient_document',
						patient_name: patientName
					};
					setAttachments([...attachments, newAttachment]);
				};
				reader.readAsDataURL(file);
			}
		} catch (error) {
			console.error('Error adding document:', error);
			alert('Failed to add document');
		}
	};

	const removePatient = (patientId: number) => {
		setSelectedPatients(selectedPatients.filter(p => p.id !== patientId));
	};

	// Get Material Icon for file type
	const getFileTypeIcon = (fileName: string): { icon: string; color: string } => {
		const ext = fileName.split('.').pop()?.toLowerCase() || '';
		
		const iconMap: Record<string, { icon: string; color: string }> = {
			// Documents
			'pdf': { icon: 'picture_as_pdf', color: '#d32f2f' },
			'doc': { icon: 'description', color: '#1976d2' },
			'docx': { icon: 'description', color: '#1976d2' },
			'txt': { icon: 'article', color: '#757575' },
			'rtf': { icon: 'article', color: '#757575' },
			// Spreadsheets
			'xls': { icon: 'table_chart', color: '#388e3c' },
			'xlsx': { icon: 'table_chart', color: '#388e3c' },
			'csv': { icon: 'table_chart', color: '#388e3c' },
			// Images
			'jpg': { icon: 'image', color: '#f57c00' },
			'jpeg': { icon: 'image', color: '#f57c00' },
			'png': { icon: 'image', color: '#f57c00' },
			'gif': { icon: 'image', color: '#f57c00' },
			'bmp': { icon: 'image', color: '#f57c00' },
			'svg': { icon: 'image', color: '#f57c00' },
			// Archives
			'zip': { icon: 'folder_zip', color: '#616161' },
			'rar': { icon: 'folder_zip', color: '#616161' },
			'7z': { icon: 'folder_zip', color: '#616161' },
			// Other
			'ppt': { icon: 'slideshow', color: '#d84315' },
			'pptx': { icon: 'slideshow', color: '#d84315' },
			'html': { icon: 'code', color: '#0288d1' },
			'xml': { icon: 'code', color: '#0288d1' },
		};
		
		return iconMap[ext] || { icon: 'insert_drive_file', color: '#757575' };
	};

	return (
		<div className="fixed inset-0 z-50 bg-base-100 flex flex-col">
			{/* Header with From/Template/Subject/Signature */}
			<div className="bg-base-200 border-b border-base-300 px-6 py-3 shrink-0">
				{/* Top Row: Title + From/Template/Subject/Signature + Actions */}
				<div className="flex items-center justify-between gap-4 mb-3">
			<div className="flex items-center gap-4 flex-1 min-w-0">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaEdit className="text-primary" />
					Compose Email
				</h1>
				
				{/* From */}
				<div className="flex items-center gap-2">
					<label className="text-sm font-semibold whitespace-nowrap">From:</label>
					<select
						value={fromEmail}
						onChange={(e) => setFromEmail(e.target.value)}
						className="select select-bordered w-56"
					>
						{clinicSettings?.use_clinic_email && clinicSettings?.clinic_email && (
							<option value={clinicSettings.clinic_email}>
								{clinicSettings.clinic_email} (Clinic)
							</option>
						)}
						{userEmail && (
							<option value={userEmail}>
								{userEmail} (Personal)
							</option>
						)}
					</select>
				</div>

				{/* Email Template */}
				<div className="flex items-center gap-2">
					<label className="text-sm font-semibold whitespace-nowrap">Template:</label>
					<select
						value={selectedTemplateId}
						onChange={(e) => handleTemplateSelect(e.target.value)}
						className="select select-bordered w-48"
					>
						<option value="">None</option>
						{templates.map(template => (
							<option key={template.id} value={template.id}>
								{template.name}
							</option>
						))}
					</select>
				</div>

				{/* Signature */}
				<div className="flex items-center gap-2">
					<label className="text-sm font-semibold whitespace-nowrap">Signature:</label>
					<select
						value={selectedSignatureId}
						onChange={(e) => handleSignatureSelect(e.target.value)}
						className="select select-bordered w-44"
					>
						<option value="">None</option>
						{signatures.map(signature => (
							<option key={signature.id} value={signature.id}>
								{signature.name}
							</option>
						))}
					</select>
				</div>
			</div>
			
			{/* Action Buttons */}
			<div className="flex items-center gap-2 shrink-0">
				<button
					onClick={handleSaveDraft}
					disabled={savingDraft}
					className="btn btn-ghost gap-2"
				>
					<FiSave className="w-4 h-4" />
					{savingDraft ? 'Saving...' : 'Save Draft'}
				</button>
				<button
					onClick={handleSendEmail}
					disabled={sending}
					className="btn btn-primary gap-2"
				>
					<FiSend className="w-4 h-4" />
					{sending ? 'Sending...' : 'Send Email'}
				</button>
				<button
					onClick={handleCancel}
					className="btn btn-ghost btn-square"
				>
					<FiX className="w-5 h-5" />
				</button>
			</div>
		</div>

		{/* To/Subject Row */}
		<div className="flex items-start gap-4 mb-2">
			{/* Left: To field (half width) */}
			<label className="text-sm font-semibold whitespace-nowrap mt-3 w-[28px]">To:</label>
			<div className="flex-1 min-w-0">
				<RecipientSelector
					label=""
					selectedRecipients={toRecipients}
					onChange={setToRecipients}
					placeholder="Add recipients..."
					allowMultiple={true}
					compact={true}
				/>
			</div>
			
			{/* CC/BCC Toggle buttons */}
			<div className="flex gap-2 shrink-0">
				{!showCc && (
					<button
						type="button"
						onClick={() => setShowCc(true)}
						className="btn btn-ghost btn-xs"
					>
						+Cc
					</button>
				)}
				{!showBcc && (
					<button
						type="button"
						onClick={() => setShowBcc(true)}
						className="btn btn-ghost btn-xs"
					>
						+Bcc
					</button>
				)}
			</div>
			
			{/* Right: Subject field (half width) */}
			<label className="text-sm font-semibold whitespace-nowrap self-center">Subject:</label>
			<input
				type="text"
				value={subject}
				onChange={(e) => setSubject(e.target.value)}
				placeholder="Email subject..."
				className="input input-bordered flex-1 min-w-0"
			/>
		</div>
		
	{/* CC Row (if shown) */}
	{showCc && (
		<div className="flex items-start gap-4 mb-2">
			<label className="text-sm font-semibold whitespace-nowrap mt-3 w-[28px]">Cc:</label>
				<div className="flex-1">
					<RecipientSelector
						label=""
						selectedRecipients={ccRecipients}
						onChange={setCcRecipients}
						placeholder="Add Cc recipients..."
						allowMultiple={true}
						compact={true}
					/>
				</div>
			</div>
		)}
		
		{/* CC Row (if shown) */}
		{showCc && (
			<div className="flex items-start gap-4 mb-2">
				<label className="text-sm font-semibold whitespace-nowrap mt-3 w-[28px]">Cc:</label>
				<div className="flex-1">
					<RecipientSelector
						label=""
						selectedRecipients={bccRecipients}
						onChange={setBccRecipients}
						placeholder="Add Bcc recipients..."
						allowMultiple={true}
						compact={true}
					/>
				</div>
			</div>
		)}
	</div>

	{/* Email Form - 3 Column Layout */}
		<div className="flex-1 flex overflow-hidden">
				{/* Left Sidebar - Attachments Only */}
				<div 
					style={{ width: `${leftSidebarWidth}px` }}
					className="border-r border-base-300 overflow-y-auto shrink-0 bg-base-200 relative"
				>
					{/* Resize Handle */}
					<div
						className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/50 active:bg-primary z-10"
						onMouseDown={(e) => {
							const startX = e.clientX;
							const startWidth = leftSidebarWidth;
							
							const handleMouseMove = (moveEvent: MouseEvent) => {
								const newWidth = Math.max(250, Math.min(600, startWidth + (moveEvent.clientX - startX)));
								setLeftSidebarWidth(newWidth);
							};
							
							const handleMouseUp = () => {
								document.removeEventListener('mousemove', handleMouseMove);
								document.removeEventListener('mouseup', handleMouseUp);
							};
							
							document.addEventListener('mousemove', handleMouseMove);
							document.addEventListener('mouseup', handleMouseUp);
						}}
					/>
				
				<div className="p-4">
					{/* Patient Search Section */}
					<div className="mb-6 relative">
						<h3 className="font-semibold text-lg mb-3">Patient Search</h3>
						
						{/* Patient Chips with DOB */}
						{selectedPatients.length > 0 && (
							<div className="flex flex-wrap gap-2 mb-3">
								{selectedPatients.map((patient) => (
									<div
										key={patient.id}
										className="flex items-center gap-2 bg-primary text-primary-content px-3 py-1.5 rounded-full text-sm font-medium"
									>
										<span>{patient.first_name} {patient.last_name}</span>
										{patient.date_of_birth && (
											<span className="opacity-80 text-xs">(DOB: {patient.date_of_birth})</span>
										)}
										<button
											type="button"
											onClick={() => removePatient(patient.id)}
											className="hover:bg-base-content/20 rounded-full p-0.5 ml-1"
										>
											<FiX className="w-3 h-3" />
										</button>
									</div>
								))}
							</div>
						)}
						
						{/* Patient Search Input */}
						<input
							type="text"
							value={patientSearch}
							onChange={(e) => setPatientSearch(e.target.value)}
							onFocus={() => {
								if (patientResults.length > 0) setShowPatientResults(true);
							}}
							placeholder="Search by name, MRN..."
							className="input input-bordered w-full"
						/>
						
						{/* Patient Search Results Dropdown */}
						{showPatientResults && patientResults.length > 0 && (
							<div className="absolute top-full left-0 right-0 mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-60 overflow-y-auto z-50">
								{patientResults.map((patient) => (
									<div
										key={patient.id}
										onClick={() => handlePatientSelect(patient)}
										className="px-3 py-2 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
									>
										<div className="font-medium">
											{patient.first_name} {patient.last_name}
										</div>
										<div className="text-xs opacity-60 space-y-0.5">
											<div>DOB: {patient.date_of_birth || 'N/A'}</div>
											{patient.medical_record_number && (
												<div>MRN: {patient.medical_record_number}</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Attachments Header */}
					<div className="flex items-center justify-between mb-4">
						<h3 className="font-semibold text-lg flex items-center gap-2">
							<FiPaperclip className="w-5 h-5" />
							Attachments {attachments.length > 0 && `(${attachments.length})`}
						</h3>
						<button
							type="button"
							onClick={() => fileInputRef.current?.click()}
							className="btn btn-primary btn-sm gap-2"
						>
							<FiUpload className="w-4 h-4" />
							Upload Files
						</button>
					</div>

					<input
						ref={fileInputRef}
						type="file"
						multiple
						onChange={handleFileSelect}
						className="hidden"
					/>

					{/* Attachments List */}
					{attachments.length > 0 ? (
						<div className="space-y-2">
							{attachments.map((attachment, index) => {
								const fileTypeInfo = getFileTypeIcon(attachment.file_name);
								return (
									<div
										key={index}
										className="flex items-center justify-between bg-base-100 p-2 rounded-lg border border-base-300"
									>
										<div className="flex items-center gap-2 min-w-0 flex-1">
											<span 
												className="material-symbols-outlined shrink-0" 
												style={{ fontSize: '20px', color: fileTypeInfo.color }}
											>
												{fileTypeInfo.icon}
											</span>
											<div className="min-w-0 flex-1">
											{attachment.patient_name && (
												<div className="text-xs opacity-50 mb-1">
													{attachment.patient_name}
												</div>
											)}
											{editingAttachment === index ? (
												<input
													type="text"
													value={editedFileName}
													onChange={(e) => setEditedFileName(e.target.value)}
													onBlur={() => {
														if (editedFileName.trim()) {
															const updatedAttachments = [...attachments];
															updatedAttachments[index].file_name = editedFileName.trim();
															setAttachments(updatedAttachments);
														}
														setEditingAttachment(null);
													}}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															e.currentTarget.blur();
														} else if (e.key === 'Escape') {
															setEditingAttachment(null);
														}
													}}
													className="input input-bordered input-xs w-full text-xs"
													autoFocus
												/>
											) : (
												<div 
													className="text-xs truncate cursor-pointer hover:text-primary"
													onClick={() => {
														setEditingAttachment(index);
														setEditedFileName(attachment.file_name);
													}}
													title={attachment.file_name}
												>
													{attachment.file_name}
												</div>
											)}
											<div className="text-xs opacity-60">
												{formatFileSize(attachment.file_size)}
											</div>
										</div>
									</div>
									<button
										type="button"
										onClick={() => handleRemoveAttachment(index)}
										className="btn btn-ghost btn-xs btn-square text-error shrink-0"
									>
										<FiTrash2 className="w-3 h-3" />
									</button>
								</div>
							);
							})}
						</div>
					) : (
						<div className="text-center py-12 text-sm opacity-60 bg-base-100 rounded-lg border border-base-300 border-dashed">
							<FiUpload className="w-12 h-12 mx-auto mb-3 opacity-40" />
							<div>No files attached</div>
							<div className="text-xs mt-1">Click "Upload Files" to add attachments</div>
						</div>
					)}
				</div>
			</div>				{/* Center - Email Body Editor (Full Height) */}
				<div className="flex-1 min-h-0">
					<EmailEditor
						ref={editorRef}
						initialContent={body}
						onChange={setBody}
						isReadOnly={false}
						height="100%"
					/>
				</div>

				{/* Right Sidebar - Patient Document Search */}
				<div 
					style={{ width: `${rightSidebarWidth}px` }}
					className="border-l border-base-300 overflow-y-auto shrink-0 bg-base-200 relative"
				>
					{/* Resize Handle */}
					<div
						className="absolute top-0 left-0 w-1 h-full cursor-col-resize hover:bg-primary/50 active:bg-primary z-10"
						onMouseDown={(e) => {
							const startX = e.clientX;
							const startWidth = rightSidebarWidth;
							
							const handleMouseMove = (moveEvent: MouseEvent) => {
								const newWidth = Math.max(300, Math.min(700, startWidth - (moveEvent.clientX - startX)));
								setRightSidebarWidth(newWidth);
							};
							
							const handleMouseUp = () => {
								document.removeEventListener('mousemove', handleMouseMove);
								document.removeEventListener('mouseup', handleMouseUp);
							};
							
							document.addEventListener('mousemove', handleMouseMove);
							document.addEventListener('mouseup', handleMouseUp);
						}}
					/>

				<div className="p-4">
					{/* Search Filter - Always visible */}
					<div className="mb-4">
						<input
							type="text"
							value={documentFilter}
							onChange={(e) => setDocumentFilter(e.target.value)}
							placeholder="Search documents (title or content)..."
							className="input input-bordered w-full"
							disabled={selectedPatients.length === 0}
						/>
					</div>

					{/* Header */}
					<div className="mb-3">
						<h3 className="font-semibold text-lg">Patient Documents</h3>
					</div>						{/* Document Count */}
						{selectedPatients.length > 0 && (() => {
							const totalDocs = selectedPatients.reduce((sum, p) => {
								const docs = patientDocuments.get(p.id) || [];
								const filtered = docs.filter(doc => 
									documentFilter === '' || 
									doc.title.toLowerCase().includes(documentFilter.toLowerCase()) ||
									doc.file_name.toLowerCase().includes(documentFilter.toLowerCase())
								);
								return sum + filtered.length;
							}, 0);
							return (
								<div className="text-sm opacity-60 mb-3">
									Found {totalDocs} document{totalDocs !== 1 ? 's' : ''}
								</div>
							);
						})()}
						
						{/* Display documents for each selected patient */}
						{selectedPatients.map(patient => {
							const docs = patientDocuments.get(patient.id) || [];
							const filteredDocs = docs.filter(doc => 
								documentFilter === '' || 
								doc.title.toLowerCase().includes(documentFilter.toLowerCase()) ||
								doc.file_name.toLowerCase().includes(documentFilter.toLowerCase())
							);
							const isLoading = loadingDocuments.has(patient.id);
							const patientName = `${patient.first_name} ${patient.last_name}`;
							
							if (filteredDocs.length === 0 && !isLoading) return null;
							
							return (
								<div key={patient.id} className="mb-4 border-t border-base-300 pt-4">
									<div className="flex items-center justify-between mb-2">
										<h4 className="font-medium text-sm">{patientName}</h4>
										<button
											onClick={() => removePatient(patient.id)}
											className="btn btn-ghost btn-xs"
											title="Remove patient"
										>
											<FiX className="w-3 h-3" />
										</button>
									</div>
									
									{isLoading ? (
										<div className="text-xs opacity-60 text-center py-2">
											Loading documents...
										</div>
									) : filteredDocs.length > 0 ? (
										<div className="space-y-1">
											{filteredDocs.map(doc => (
												<div
													key={doc.id}
													className="flex items-center justify-between bg-base-100 p-2 rounded hover:bg-base-200 cursor-pointer group"
													onClick={() => handleAddDocumentToAttachments(doc, patientName)}
													title="Click to add to attachments"
												>
													<div className="flex items-center gap-2 min-w-0 flex-1">
														<FiFile className="w-3 h-3 opacity-60 shrink-0" />
														<div className="min-w-0 flex-1">
															<div className="text-xs truncate" title={doc.title}>
																{doc.title}
															</div>
															<div className="text-xs opacity-50">
																{new Date(doc.document_date).toLocaleDateString()}
															</div>
														</div>
													</div>
													<FiChevronLeft className="w-4 h-4 opacity-0 group-hover:opacity-100 shrink-0 text-primary" />
												</div>
											))}
										</div>
									) : null}
								</div>
							);
						})}

						{selectedPatients.length === 0 && (
							<div className="text-center py-12 text-sm opacity-60">
								Search for a patient to view their documents
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ComposeEmail;
